import { useEffect, useState } from "react";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { useVault } from "@/context/VaultContext";
import { blobToDataUrl, identifier } from "@/lib/util";
import { SignaturePad } from "./SignaturePad";
import { WatermarkPreview } from "./WatermarkPreview";
import { SecurityPanel } from "./SecurityPanel";

function ImagePick({ label, value, onChange, testid }) {
  const pick = async (e) => { const f = e.target.files[0]; e.target.value = ""; if (f) onChange(await blobToDataUrl(f)); };
  return (
    <div className="flex items-center gap-3">
      <div className="h-16 w-28 rounded-md border bg-[repeating-conic-gradient(#ddd_0_25%,#fff_0_50%)] bg-[length:12px_12px] flex items-center justify-center overflow-hidden">
        {value ? <img src={value} alt={label} className="max-h-full max-w-full" data-testid={`${testid}-preview`} /> : <span className="text-[10px] text-neutral-500">none</span>}
      </div>
      <label className="inline-flex items-center gap-1 text-sm cursor-pointer rounded-md border px-3 py-1.5 hover:bg-muted transition-colors">
        <Upload className="h-4 w-4" />Upload {label}
        <input type="file" accept="image/*" className="hidden" onChange={pick} data-testid={`${testid}-upload`} />
      </label>
      {value && <Button size="icon" variant="ghost" onClick={() => onChange("")} data-testid={`${testid}-remove`}><X className="h-4 w-4" /></Button>}
    </div>
  );
}

function Checks({ title, obj, labels, onChange, prefix }) {
  return (
    <div>
      <p className="text-xs font-mono uppercase tracking-widest text-primary mb-2">{title}</p>
      <div className="grid grid-cols-2 gap-2">
        {Object.entries(labels).map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-sm rounded-md border px-3 py-2 cursor-pointer hover:bg-muted transition-colors">
            <Checkbox checked={!!obj[k]} onCheckedChange={(v) => onChange({ ...obj, [k]: !!v })} data-testid={`${prefix}-${k}-checkbox`} />{l}
          </label>
        ))}
      </div>
    </div>
  );
}

export default function ProfileStudio({ open, onOpenChange }) {
  const { profile, setProfile } = useVault();
  const [p, setP] = useState(profile);
  useEffect(() => { if (open) setP(profile); }, [open, profile]);
  const set = (patch) => setP((x) => ({ ...x, ...patch }));
  const save = async () => { await setProfile(p); toast.success("Creator Profile saved"); onOpenChange(false); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="profile-studio-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Creator Profile & Watermark Studio</DialogTitle>
          <DialogDescription>Your identity stamp for notes, media, filenames and PDFs. Stored only on this device.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="profile">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="profile" data-testid="studio-tab-profile">Profile</TabsTrigger>
            <TabsTrigger value="watermark" data-testid="studio-tab-watermark">Watermark</TabsTrigger>
            <TabsTrigger value="security" data-testid="studio-tab-security">Security</TabsTrigger>
          </TabsList>
          <TabsContent value="profile" className="space-y-5 pt-4">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2 space-y-1.5"><Label>Full Name</Label><Input value={p.fullName} onChange={(e) => set({ fullName: e.target.value })} data-testid="profile-fullname-input" /></div>
              <div className="space-y-1.5"><Label>Initials</Label><Input value={p.initials} maxLength={6} onChange={(e) => set({ initials: e.target.value.toUpperCase() })} data-testid="profile-initials-input" /></div>
            </div>
            <div className="space-y-2"><Label>Digital Signature</Label><SignaturePad onSave={(s) => set({ signature: s })} />
              <ImagePick label="signature" value={p.signature} onChange={(v) => set({ signature: v })} testid="signature" /></div>
            <div className="space-y-2"><Label>Custom Logo (transparent PNG supported)</Label>
              <ImagePick label="logo" value={p.logo} onChange={(v) => set({ logo: v })} testid="logo" /></div>
          </TabsContent>
          <TabsContent value="watermark" className="space-y-5 pt-4">
            <WatermarkPreview profile={p} />
            <Checks title="Stamp these" obj={p.stamp} onChange={(stamp) => set({ stamp })} prefix="stamp"
              labels={{ name: "Full Name", initials: "Initials", signature: "Signature", logo: "Transparent Logo" }} />
            <Checks title="Apply to" obj={p.apply} onChange={(apply) => set({ apply })} prefix="apply"
              labels={{ notes: "Notes", media: "Media watermarks", filenames: "Filenames", pdfs: "PDFs" }} />
            <div>
              <div className="flex justify-between text-sm mb-2"><Label>Watermark opacity</Label><span className="font-mono" data-testid="opacity-value">{Math.round(p.opacity * 100)}%</span></div>
              <Slider value={[p.opacity * 100]} min={5} max={100} step={5} onValueChange={([v]) => set({ opacity: v / 100 })} data-testid="watermark-opacity-slider" />
            </div>
            <p className="text-xs font-mono text-muted-foreground" data-testid="filename-preview">Filename prefix: {identifier(p)}_Folder_Title_REF_YYYY-MM-DD_HHMMSS.ext</p>
          </TabsContent>
          <TabsContent value="security" className="pt-4"><SecurityPanel /></TabsContent>
        </Tabs>
        <div className="sticky -bottom-6 -mx-6 -mb-6 px-6 py-3 border-t bg-background/95 backdrop-blur flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="profile-cancel-button">Close</Button>
          <Button onClick={save} data-testid="profile-save-button">Save Profile</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
